// ProgressDashboard.tsx - the all-time progress rollup (S25), a pure function of a ProgressDashboard
// (GET /api/progress). Reference lane #4: Chess.com Insights/Stats. Names the biggest leak + sharpest
// skill (North Star: "names your biggest leak"), then the grade cuts by street/villain/depth/position
// and the skill dimensions, each as move-class-colored bars. Empty states are honest (bestSkill can be
// null; a cut with no data for a canonical key shows a dashed track, per Joe's S25 sign-off).
import type { ProgressDashboard as Dashboard } from '@postflop/schema';
import { toneVar, fmt1, barPct } from './grade';

/** Canonical street order so a not-yet-played street renders as a dashed "no data" row. */
const STREET_ORDER = ['preflop', 'flop', 'turn', 'river'] as const;
const STREET_LABEL: Record<string, string> = { preflop: 'Preflop', flop: 'Flop', turn: 'Turn', river: 'River' };
/** Poker seat order for the position cut (present keys only; no dashed fill for unplayed seats). */
const POS_ORDER = ['UTG', 'UTG1', 'MP', 'MP1', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];

const titleCase = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const stackBB = (k: string): number => parseInt(k, 10) || 0;
/** Short one-word labels for the known skill dimensions so the 52px label column never wraps
 *  (matches the S25 mockup). Unknown dimensions fall back to title case. */
const SKILL_LABEL: Record<string, string> = {
  value_extraction: 'Value',
  fold_discipline: 'Folds',
  bluff_selection: 'Bluffs',
};
const skillLabel = (d: string): string => SKILL_LABEL[d] ?? titleCase(d);

interface Row {
  label: string;
  grade: number | null; // null => no data (dashed track)
}

function Bars({ rows }: { rows: Row[] }) {
  return (
    <>
      {rows.map((r) => (
        <div key={r.label} className={'crow' + (r.grade === null ? ' empty' : '')}>
          <span className="cl">{r.label}</span>
          <span className="track">
            {r.grade === null ? (
              <span className="none" />
            ) : (
              <span className="fill" style={{ width: `${barPct(r.grade)}%`, background: toneVar(r.grade) }} />
            )}
          </span>
          <span className="cv" style={r.grade === null ? undefined : { color: toneVar(r.grade) }}>
            {r.grade === null ? '--' : fmt1(r.grade)}
          </span>
        </div>
      ))}
    </>
  );
}

/** Build ordered rows for a record cut. `order` gives canonical keys (missing => dashed); extras append. */
function cutRows(
  cut: Record<string, number> | undefined,
  order: readonly string[],
  label: (k: string) => string,
  fillMissing: boolean,
): Row[] {
  const data = cut ?? {};
  const seen = new Set<string>();
  const rows: Row[] = [];
  for (const k of order) {
    if (k in data) {
      rows.push({ label: label(k), grade: data[k] });
      seen.add(k);
    } else if (fillMissing) {
      rows.push({ label: label(k), grade: null });
      seen.add(k);
    }
  }
  // append any present keys not in the canonical order (keeps the cut complete)
  for (const k of Object.keys(data)) if (!seen.has(k)) rows.push({ label: label(k), grade: data[k] });
  return rows;
}

export function ProgressDashboard({ data }: { data: Dashboard }) {
  const { totals, leaks, bestSkill, skillDimensions } = data;
  const topLeak = leaks[0] ?? null;
  const otherLeaks = leaks.slice(1);

  // depth cut ascending by bb; position cut in seat order; archetype title-cased.
  const depthRows = cutRows(data.gradeByStackDepth, [], (k) => k, false).sort(
    (a, b) => stackBB(a.label) - stackBB(b.label),
  );
  const posRows = cutRows(data.gradeByPosition, POS_ORDER, (k) => k, false);
  const archRows = cutRows(data.gradeByArchetype, [], titleCase, false);
  const streetRows = cutRows(data.gradeByStreet, STREET_ORDER, (k) => STREET_LABEL[k] ?? k, true);
  const sortedSkills = [...skillDimensions].sort((a, b) => b.avg - a.avg);
  const skillRows: Row[] = sortedSkills.map((d) => ({ label: skillLabel(d.dimension), grade: d.avg }));
  const leadingSkill = sortedSkills[0] ? titleCase(sortedSkills[0].dimension) : null;

  return (
    <div className="prog">
      <div>
        <div className="kicker">Your progress</div>
        <div className="h2">Where your game stands</div>
        <div className="lede num">
          {totals.hands} hands . {totals.decisions} decisions logged . Local $1/$3
        </div>
      </div>

      <div className="slab">The one thing to fix, and your sharpest edge</div>
      <div className="pair">
        <div className="insight leak">
          <div className="ilab">Biggest leak</div>
          <div className="istat">{topLeak ? topLeak.statement : 'No leak yet'}</div>
          <div className="isub">
            {topLeak && (
              <>
                <span className="tag num">seen {topLeak.count}x</span>
                <div className="drill">Now weighted into what you get dealt</div>
              </>
            )}
          </div>
        </div>
        <div className={'insight skill' + (bestSkill ? '' : ' empty')}>
          <div className="ilab">Sharpest skill</div>
          <div className="istat">{bestSkill ? titleCase(bestSkill) : 'Not enough hands yet'}</div>
          <div className="isub">
            {bestSkill
              ? 'Your most reliable edge across enough hands to trust it.'
              : `Locks in at ~30 decisions in one skill.${leadingSkill ? ` ${leadingSkill} is leading so far.` : ''}`}
          </div>
        </div>
      </div>

      {otherLeaks.length > 0 && (
        <div className="cut">
          <h4>Also watch</h4>
          {otherLeaks.map((l) => (
            <div key={l.code} className="alsorow">
              <span className="dotm" style={{ background: 'var(--mist)' }} />
              <span className="alt">{l.statement}</span>
              <span className="cv num">{l.count}x</span>
            </div>
          ))}
        </div>
      )}

      <div className="slab">Grade by cut (avg decision grade, out of 10)</div>

      {streetRows.length > 0 && (
        <div className="cut">
          <h4>
            By street <span className="n num">{totals.decisions} decisions</span>
          </h4>
          <Bars rows={streetRows} />
        </div>
      )}
      {archRows.length > 0 && (
        <div className="cut">
          <h4>By villain type</h4>
          <Bars rows={archRows} />
        </div>
      )}
      {depthRows.length > 0 && (
        <div className="cut">
          <h4>By stack depth</h4>
          <Bars rows={depthRows} />
        </div>
      )}
      {posRows.length > 0 && (
        <div className="cut">
          <h4>By position</h4>
          <Bars rows={posRows} />
        </div>
      )}
      {skillRows.length > 0 && (
        <div className="cut">
          <h4>
            Skill dimensions <span className="n">by touched decisions</span>
          </h4>
          <Bars rows={skillRows} />
        </div>
      )}

      <div className="trend">
        <div className="ic">^</div>
        <div>
          <div className="tt">Trends unlock at 100 decisions</div>
          <div className="ts num">
            {totals.decisions} logged so far. Week-over-week movement appears once there is enough to compare.
          </div>
        </div>
      </div>
    </div>
  );
}
